import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  Button,
  Form,
  InputGroup,
  ListGroup,
  Badge,
} from "react-bootstrap";
import ImageWithLoader from "../components/image";
import CustomLoader from "../components/loader";
import { toast } from "react-toastify";
import {
  fetchProducts,
  getWeeklyFeaturedProducts,
  saveWeeklyFeaturedProducts,
} from "../api/axios/requests";

const MAX = 8;

export default function WeeklyBestProductsModal({ show, handleClose, onSuccess }) {
  const [search, setSearch]       = useState("");
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [list, setList]           = useState([]);
  const [page, setPage]           = useState(1);
  const [hasMore, setHasMore]     = useState(false);
  const [selectedIds, setSelectedIds]   = useState(() => new Set());
  const [selectedMap, setSelectedMap]   = useState({});

  const load = async (pageToLoad = 1, isRefresh = false) => {
    try {
      setLoading(true);
      const res = await fetchProducts({ page: pageToLoad, categoryId: null, keyword: search });
      const rows = Array.isArray(res?.data) ? res.data : [];
      if (isRefresh) setList(rows);
      else setList((prev) => [...prev, ...rows]);
      setHasMore(res?.pagination?.currentPage < res?.pagination?.totalPages);
      setPage(res?.pagination?.currentPage || pageToLoad);
    } catch (e) {
      console.error("weekly products fetch error:", e);
    } finally {
      setLoading(false);
    }
  };

  // Нээгдэх бүрт өмнө сонгосон бараануудыг ачаална
  useEffect(() => {
    if (!show) return;
    setSearch("");
    setList([]);
    setSelectedIds(new Set());
    setSelectedMap({});

    getWeeklyFeaturedProducts()
      .then((res) => {
        if (!res?.success) return;
        const ids = res.data?.productIds || [];
        if (!ids.length) return;
        setSelectedIds(new Set(ids));
        // product details later merged from list
        const partial = (res.data?.products || []).reduce((m, p) => {
          m[p.id] = p;
          return m;
        }, {});
        setSelectedMap(partial);
      })
      .catch(() => {});

    load(1, true);
  }, [show]);

  // list шинэчлэгдэх бүрт selectedMap-ийг баяжуулна
  useEffect(() => {
    if (!show || !list.length) return;
    setSelectedMap((prev) => {
      const next = { ...prev };
      list.forEach((p) => {
        if (selectedIds.has(p.id) && !next[p.id]) next[p.id] = p;
      });
      return next;
    });
  }, [list, show]);

  // Хайлт өөрчлөгдөхөд дахин ачаална
  useEffect(() => {
    if (!show) return;
    const t = setTimeout(() => { setList([]); load(1, true); }, 300);
    return () => clearTimeout(t);
  }, [search, show]);

  const toggleSelect = (id, obj) => {
    setSelectedIds((prev) => {
      if (prev.has(id)) {
        const next = new Set(prev);
        next.delete(id);
        return next;
      }
      if (prev.size >= MAX) {
        toast.warning(`Хамгийн ихдээ ${MAX} бараа сонгох боломжтой.`);
        return prev;
      }
      return new Set([...prev, id]);
    });
    setSelectedMap((prev) => {
      const next = { ...prev };
      if (next[id]) delete next[id];
      else next[id] = obj;
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setSelectedMap({});
  };

  const selectedArr = useMemo(() => Object.values(selectedMap), [selectedMap]);

  const handleSave = async () => {
    try {
      setSaving(true);
      // Дарааллыг хадгалах: selectedIds-ийн оруулах дарааллаар
      const orderedIds = [...selectedIds];
      const res = await saveWeeklyFeaturedProducts(orderedIds);
      if (res?.success) {
        toast.success("Бараанууд амжилттай хадгалагдлаа.");
        handleClose();
        onSuccess?.();
      } else {
        toast.error(res?.message || "Хадгалахад алдаа гарлаа.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || "Серверийн алдаа гарлаа.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="mdi mdi-package-variant me-2 text-warning" />
          Нүүр дэлгэцэнд гарах бараа сонгох
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
          E24 аппликейшны нүүр дэлгэц дээр танай байгууллагын хэсэгт харагдах барааг сонгоно уу.
          Хамгийн ихдээ <strong>{MAX}</strong> бараа сонгох боломжтой.
        </p>

        {/* Сонгогдсон бараанууд */}
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex flex-wrap" style={{ gap: 8, flex: 1 }}>
            {selectedArr.length ? (
              selectedArr.map((it) => (
                <div
                  key={it.id}
                  className="d-flex align-items-center"
                  style={{ border: "1px solid #ddd", borderRadius: 6, padding: "4px 8px", cursor: "pointer" }}
                  onClick={() => toggleSelect(it.id, it)}
                  title="Хасах"
                >
                  <ImageWithLoader src={it.image} width="26px" height="26px" className="me-2 rounded" />
                  <span style={{ maxWidth: 120, fontSize: 13 }} className="text-truncate">{it.name}</span>
                  <i className="mdi mdi-close ms-1 text-danger" style={{ fontSize: 12 }} />
                </div>
              ))
            ) : (
              <span className="text-muted" style={{ fontSize: 13 }}>Сонгогдсон бараа алга</span>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 ms-2 flex-shrink-0">
            <Badge bg={selectedArr.length >= MAX ? "warning" : "secondary"}>
              {selectedArr.length}/{MAX}
            </Badge>
            <Button variant="light" size="sm" disabled={!selectedArr.length} onClick={clearSelection}>
              Цэвэрлэх
            </Button>
          </div>
        </div>

        {/* Хайлт */}
        <InputGroup className="mb-3">
          <Form.Control
            placeholder="Бараа хайх..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button variant="outline-secondary" onClick={() => { setList([]); load(1, true); }}>
            <i className="mdi mdi-magnify" />
          </Button>
        </InputGroup>

        {/* Бараануудын жагсаалт */}
        <div style={{ maxHeight: 380, overflowY: "auto", border: "1px solid #eee", borderRadius: 6 }}>
          {loading && !list.length ? (
            <div className="text-center p-4"><CustomLoader fontSize={26} /></div>
          ) : (
            <ListGroup variant="flush">
              {list.map((it) => {
                const checked = selectedIds.has(it.id);
                const limitReached = !checked && selectedIds.size >= MAX;
                return (
                  <ListGroup.Item
                    key={it.id}
                    className="d-flex align-items-center"
                    style={{ opacity: limitReached ? 0.5 : 1, cursor: limitReached ? "not-allowed" : "pointer" }}
                    onClick={() => !limitReached && toggleSelect(it.id, it)}
                  >
                    <Form.Check
                      className="me-3"
                      type="checkbox"
                      checked={checked}
                      disabled={limitReached}
                      onChange={() => !limitReached && toggleSelect(it.id, it)}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <ImageWithLoader src={it.image} width="40px" height="40px" className="me-3 rounded" />
                    <div className="flex-grow-1">
                      <strong>{it.name}</strong>
                      <div className="text-muted" style={{ fontSize: 13 }}>
                        {Number(it.price ?? 0).toLocaleString()}₮
                      </div>
                    </div>
                    {checked && <i className="mdi mdi-check-circle text-success" style={{ fontSize: 20 }} />}
                  </ListGroup.Item>
                );
              })}

              {hasMore && (
                <ListGroup.Item className="text-center">
                  <Button variant="light" size="sm" disabled={loading} onClick={() => load(page + 1)}>
                    {loading ? <CustomLoader fontSize={18} /> : "Дараагийнх"}
                  </Button>
                </ListGroup.Item>
              )}

              {!loading && !list.length && (
                <ListGroup.Item className="text-center text-muted py-4">
                  Илэрц олдсонгүй
                </ListGroup.Item>
              )}
            </ListGroup>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={saving}>Хаах</Button>
        <Button variant="warning" onClick={handleSave} disabled={saving || !selectedArr.length}>
          {saving
            ? <><i className="mdi mdi-loading mdi-spin me-1" />Хадгалж байна…</>
            : <><i className="mdi mdi-content-save me-1" />Хадгалах</>}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
